import { AspectRatio, Box, Flex, Heading, HStack } from "@chakra-ui/layout";
import { Text, Button, useColorMode } from "@chakra-ui/react";
import * as React from "react";
import { useHistory } from "react-router";
import { okhiTracker } from "../OkHiTracker";
import { useAcquireAddressState } from "./AcquireAddressStateProvider";
import {
  useAddressGeopoint,
  useAddressStreetviewData,
} from "./AddressStateProvider";
import { useOkHiIncomingMessage } from "./OkHiIncomingMessageProvider";

type OnboardingState = "onboarding1" | "onboarding2";

const STREET_VIEW_BASE_URL = `https://maps.googleapis.com/maps/api/streetview?key=${process.env.REACT_APP_GMAP_API_KEY}&size=640x640&fov=45`;

export function StreetviewScreen() {
  const { colorMode } = useColorMode();
  const { setAcquireAddressState } = useAcquireAddressState();
  const okhiMessage = useOkHiIncomingMessage();
  const { addressStreetviewData, setAddressStreetviewData } =
    useAddressStreetviewData();
  const { setAddressGeoPoint } = useAddressGeopoint();
  const history = useHistory();
  const [onboardingState, _setOnboardingState] =
    React.useState<OnboardingState>("onboarding1");
  const onboardingStateRef = React.useRef<OnboardingState>(onboardingState);
  const setOnboardingState = (state: OnboardingState) => {
    onboardingStateRef.current = state;
    _setOnboardingState(state);
  };
  const [panorama, setPanorama] =
    React.useState<google.maps.StreetViewPanorama | null>(null);
  const streetviewRef = React.useRef<HTMLDivElement>(null);
  React.useEffect(() => {
    okhiTracker.page();
  }, []);
  React.useEffect(() => {
    let streetviewElListener: google.maps.MapsEventListener | null = null;
    const panoId = addressStreetviewData?.panoId;
    const streetviewEl = streetviewRef.current;
    if (panoId != null && streetviewEl != null) {
      const panorama = new window.google.maps.StreetViewPanorama(streetviewEl, {
        pano: panoId,
        disableDefaultUI: true,
        enableCloseButton: false,
        linksControl: true,
        clickToGo: false,
        visible: true,
        motionTracking: false,
        motionTrackingControl: false,
      });
      streetviewElListener = window.google.maps.event.addDomListener(
        streetviewEl,
        "pointerdown",
        () => {
          const onboardingState = onboardingStateRef.current;
          if (onboardingState === "onboarding1") {
            setOnboardingState("onboarding2");
          }
        }
      );
      setPanorama(panorama);
    }
    return () => {
      streetviewElListener?.remove();
    };
  }, [addressStreetviewData?.panoId]);
  const handleSkip = () => {
    if (okhiMessage?.context.isToTheDoorEnabled) {
      history.push("/to-the-door");
    } else {
      setAcquireAddressState("complete");
    }
    okhiTracker.track("Button Tapped", {
      onObject: "button",
      action: "tapclick",
      label: "Skip",
    });
  };
  const handleSave = async () => {
    const lat = panorama?.getLocation()?.latLng?.lat();
    const lng = panorama?.getLocation()?.latLng?.lng();
    if (lat != null && lng != null) {
      const geoPoint = { lat, lng };
      const { heading, pitch } = panorama!.getPov();
      const streetviewURL = `${STREET_VIEW_BASE_URL}&location=${lat},${lng}&heading=${heading}&pitch=${pitch}`;
      const streetviewData = {
        panoId: panorama!.getPano(),
        url: streetviewURL,
      };
      setAddressStreetviewData(streetviewData);
      setAddressGeoPoint(geoPoint);
    }
    if (okhiMessage?.context.isToTheDoorEnabled) {
      history.push("/to-the-door");
    } else {
      setAcquireAddressState("complete");
    }
    okhiTracker.track("Button Tapped", {
      onObject: "button",
      action: "tapclick",
      label: okhiMessage?.context.isToTheDoorEnabled ? "Next" : "Done",
    });
  };
  return (
    <Flex h="100%" position="relative" direction="column">
      <Box id="streetviewEl" flex="1" ref={streetviewRef} />
      <Box
        position="absolute"
        w="100%"
        h="100%"
        zIndex="1"
        pointerEvents="none">
        <Box
          boxShadow="base"
          p={3}
          backgroundColor={colorMode === "light" ? "white" : "gray.800"}>
          <Heading size="md">Find your address</Heading>
        </Box>
        {onboardingState === "onboarding1" ? (
          <Box
            position="absolute"
            top="45%"
            left="50%"
            transform="translate(-50%, -50%)"
            backgroundColor={colorMode === "light" ? "white" : "gray.800"}
            boxShadow="base"
            textAlign="center"
            opacity="85%"
            p={3}
            borderRadius="base">
            <Heading size="sm">
              Use the arrows or move the photo to walk the street
            </Heading>
          </Box>
        ) : null}
        {onboardingState === "onboarding2" ? (
          <AspectRatio
            position="absolute"
            top="50%"
            left="50%"
            transform="translate(-50%, -50%)"
            minW="250">
            <Box
              borderWidth="4px"
              borderStyle="solid"
              borderColor="red.700"
              borderRadius="base"
              sx={{
                display: "block !important",
                overflow: "visible !important",
              }}>
              <Text
                bgColor="red.700"
                color="white"
                textAlign="center"
                p={2}
                mt="-4px"
                mx="-4px">
                Place your entrance in the box
              </Text>
            </Box>
          </AspectRatio>
        ) : null}
        <HStack
          position="absolute"
          bottom={8}
          width="100%"
          left="0"
          p={3}
          spacing={2}
          align="center"
          pointerEvents="all">
          <Button
            flex={1}
            variant={
              colorMode === "light" ? "okhi-white-outline" : "okhi-dark-outline"
            }
            onClick={handleSkip}>
            Skip
          </Button>
          <Button
            flex={3}
            variant="okhi-solid"
            onClick={handleSave}
            disabled={onboardingState === "onboarding1"}>
            {okhiMessage?.context.isToTheDoorEnabled ? "Next" : "Done"}
          </Button>
        </HStack>
      </Box>
    </Flex>
  );
}
